import _ from 'lodash';
import React, { useRef } from 'react';
import { FastField, Field } from 'formik';

import { configs } from '$configs';
import { TextField, TextFieldProps } from '@mui/material';
import { FormControlWrapper } from '../../primitives/textInput/styles';
import intl from '$gintl';
import { useDispatch } from 'react-redux';
import { toastDanger, toastSuccess } from '$gbusiness/redux/toaster/actions';
import Scanner from './scanner';

interface ScanInputProps {
  icon?: string;
  label?: string;
  labelText?: string;
  name: string;
  formik?: any;
  size?: string;
  mobileDefault?: boolean;
  enableMobile?: boolean;
  hasClearIcon?: boolean;
  decimalPlaces?: number;
  enterToSubmit?: boolean;
  handleKeyPress?: Function;
  maskType?: string | undefined;
  readonly?: boolean;
  prefix?: string;
  postfix?: string;
  className?: string;
  disabled?: boolean;
  helperText?: string;
  placeholder?: string;
  position?: TextFieldProps['variant'];
  shrink?: boolean;
  rowsMax?: number;
  rows?: number;
  maxLength?: number;
  format?: string;
  defaultValue?: string;
  handleChange?: (e) => void;
  handleBlur: () => {};
  disableFastField?: boolean;
}

const ScanInput: React.FC<ScanInputProps> = ({
  icon = '',
  label = '',
  labelText = '',
  name = '',
  mobileDefault = false,
  enableMobile = true,
  size = configs.display.inputSize,
  formik = {},
  helperText = '',
  prefix = '',
  postfix = '',
  maskType = undefined,
  className = '',
  readonly = false,
  disabled = false,
  placeholder = '',
  shrink = false,
  position = undefined,
  rowsMax = 40,
  rows = 8,
  maxLength = 6,
  format = 'MM/dd/yyyy',
  defaultValue = '',
  disableFastField = true,
  handleChange = (e) => {},
  handleBlur = (e) => {},
  ...props
}) => {
  const dispatch = useDispatch();
  const inputRef = useRef<HTMLInputElement>(null);

  const onChange = (e) => {
    if (formik.handleChange) formik.handleChange(e);
    if (handleChange) handleChange(e);
  };

  const onScanSuccess = (newValue) => {
    const oldValue = inputRef.current?.value || _.get(formik.values, name, '');
    const serialsArray = oldValue ? oldValue.split(/[\n,]/) : [];
    if (serialsArray.includes(newValue.trim())) {
      dispatch(toastDanger({ text: 'Duplicate scan: ' + newValue }));
    } else {
      dispatch(toastSuccess({ text: 'Scanned ' + newValue }));
      const newSerialArray = [newValue.trim(), ...serialsArray];
      const newSerials = newSerialArray.join('\n');
      formik.setFieldValue(name, newSerials, true);
      // formik.setValues({ ...form.values, [name]: newSerials }, true);
    }
  };

  const onBlur = (e, shouldChange = false) => {
    // any blue handling goes here
    if (handleChange && shouldChange) {
      if (formik.handleChange) formik.handleChange(e);
      handleChange(e);
    }
    if (formik.handleBlur) formik.handleBlur(e);
    if (handleBlur) handleBlur(e);
  };

  const FormField = disableFastField ? Field : FastField;

  const errorKey = _.get(formik.errors, name);
  const isTouched = _.get(formik.touched, name) !== undefined;
  const hasError = isTouched && errorKey !== undefined;
  const inputSize = size === 'small' || size === 'xsmall' ? 'small' : 'medium';
  const errorMsg = hasError ? intl(`INPUT.ERROR.${errorKey}`) : undefined;

  const labelProps =
    position !== undefined
      ? {
          label: labelText || intl(label),
          InputLabelProps: {
            ...(shrink && { shrink }),
          },
        }
      : {};
  const variant = position || 'outlined';

  return (
    <>
      <FormField name={name}>
        {({ form }) => {
          const value =
            typeof _.get(form.values, name) === 'number'
              ? _.get(form.values, name)
              : _.get(form.values, name) || '';

          return (
            <>
              <FormControlWrapper
                className={`${className} ${size === 'xsmall' && 'xsmall'}`}
                size={inputSize}
                variant={variant}
                fullWidth>
                <Scanner
                  inputSize={inputSize}
                  enableMobile={enableMobile}
                  mobileDefault={mobileDefault}
                  onScanSuccess={onScanSuccess}
                  className={className}
                />
                <TextField
                  {...labelProps}
                  {...props}
                  inputRef={inputRef}
                  data-testid={name}
                  className={`${readonly && 'readonly'}`}
                  multiline={true}
                  maxRows={rowsMax}
                  rows={rows}
                  type="string"
                  name={name}
                  disabled={disabled}
                  placeholder={intl(placeholder) || undefined}
                  size={inputSize}
                  onBlur={onBlur}
                  onInput={onChange}
                  value={value}
                  error={hasError}
                  variant={variant}
                  helperText={errorMsg || helperText || ''}
                />
              </FormControlWrapper>
            </>
          );
        }}
      </FormField>
    </>
  );
};

export default ScanInput;
